import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  CreateAdminData, 
  UserManagement, 
  UserFilters 
} from '../../types';
import * as adminService from '../../services/adminService';

interface AdminState {
  users: UserManagement[];
  currentUser: UserManagement | null;
  filters: UserFilters;
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  stats: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    adminUsers: number;
    tenantUsers: number;
    newUsersThisMonth: number;
  } | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: AdminState = {
  users: [],
  currentUser: null,
  filters: {
    page: 1,
    limit: 10,
  },
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  },
  stats: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

export const createAdminAccount = createAsyncThunk(
  'admin/createAdminAccount',
  async (adminData: CreateAdminData, { rejectWithValue }) => {
    try {
      const response = await adminService.createAdminAccount(adminData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to create admin account');
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  'admin/fetchAllUsers',
  async (filters: UserFilters = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllUsers(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to fetch users');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'admin/toggleUserStatus',
  async ({ userId, isActive }: { userId: string; isActive: boolean }, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleUserStatus(userId, isActive);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to update user status');
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'admin/updateUserRole',
  async ({ userId, role, subRole }: { userId: string; role: string; subRole?: string }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUserRole(userId, role, subRole);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to update user role');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await adminService.deleteUser(userId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to delete user');
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'admin/fetchUserStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserStats();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || 'Failed to fetch user stats');
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<UserFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
      };
    },
    setCurrentUser: (state, action: PayloadAction<UserManagement | null>) => {
      state.currentUser = action.payload;
    },
    clearUsers: (state) => {
      state.users = [];
      state.currentUser = null;
      state.pagination = initialState.pagination;
    },
    updateUserInList: (state, action: PayloadAction<UserManagement>) => {
      const index = state.users.findIndex(user => user.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Admin Account
      .addCase(createAdminAccount.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createAdminAccount.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.users.unshift(action.payload);
          state.error = null;
        }
      })
      .addCase(createAdminAccount.rejected, (state, action) => {
        state.isCreating = false;
        state.error = (action.payload as string) || 'Failed to create admin account';
      })
      // Fetch All Users
      .addCase(fetchAllUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.users = action.payload.data;
          state.pagination = action.payload.pagination;
          state.error = null;
        }
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch users';
      })
      // Toggle User Status
      .addCase(toggleUserStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const user = action.payload;
          const index = state.users.findIndex(u => u.id === user.id);
          if (index !== -1) {
            state.users[index] = user;
          }
          if (state.currentUser?.id === user.id) {
            state.currentUser = user;
          }
          state.error = null;
        }
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || 'Failed to update user status';
      })
      // Update User Role
      .addCase(updateUserRole.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const user = action.payload;
          const index = state.users.findIndex(u => u.id === user.id);
          if (index !== -1) {
            state.users[index] = user;
          }
          if (state.currentUser?.id === user.id) {
            state.currentUser = user;
          }
          state.error = null;
        }
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || 'Failed to update user role';
      })
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isDeleting = false;
        if (action.payload) {
          state.users = state.users.filter(user => user.id !== action.payload);
          if (state.currentUser?.id === action.payload) {
            state.currentUser = null;
          }
          state.error = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = (action.payload as string) || 'Failed to delete user';
      })
      // Fetch User Stats
      .addCase(fetchUserStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.stats = action.payload;
          state.error = null;
        }
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch user stats';
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentUser,
  clearUsers,
  updateUserInList,
} = adminSlice.actions;

export default adminSlice.reducer;